import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axios from "axios";
import { BASE_URL } from "../config";
import { getAuditTrailData } from "../util/getAuditTrailData";
import { getRevAndTraining, uploadJsonToS3, adjustSiblingOrderAndInsert, prepareCurrentSnapshot } from "../util/AppUtils";
import { v4 as uuidv4 } from 'uuid';
import { updateCurrentSnapshot } from "./procedureRevisionSlice";
const baseUrl = BASE_URL + "attachment-tree";

// 📌 Create Attachment (POST)
// 📌 Create Attachment (POST)
export const createAttachment = createAsyncThunk(
  "attachment/create",
  async (
    {
      jsonData,
      comment,
      version_content,
      allNodesData,
      partnumber,
      revision,
    },
    { dispatch, rejectWithValue }
  ) => {
    const treeNodeId = uuidv4();
    const attachmentTreeNodeId = `att_ref_${Date.now()}`;

    const ad = await getAuditTrailData({
      action: "CREATE_ATTACHMENT", 
      comment: comment,
      version_content: {
        ...version_content,
        tree_node_id: treeNodeId,
      },
    });

    const { item_code_type, status } = JSON.parse(JSON.stringify(version_content));

    const newAttachmentNode = {
      tree_node_id: treeNodeId,
      type: "attachment",
      sibling_order: jsonData.sibling_order,
      version_id_ref: jsonData.revision_id_ref,
      part_ref: jsonData.part_required_id_ref,
      procedure_rev_ref: jsonData.revision_id_ref,
      section_id_ref: jsonData.section_id_ref,
      step_id_ref: jsonData.step_id_ref,
      substep_id_ref: jsonData.substep_id_ref || null,
      is_deleted: false,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      detail: {
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        attachments: jsonData.attachment.map((url, index) => ({
          attachment: url,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          attachment_id: `att_id_${Date.now()}_${index}`,
          attachment_tree_node_ref: attachmentTreeNodeId,
        })),
        version_id_ref: jsonData.revision_id_ref,
        short_description: jsonData.short_description,
        procedure_tree_ref: treeNodeId,
        attachment_tree_node_id: attachmentTreeNodeId,
      },
      children: [],
      isDraft: true,
    };

    const snapshotPayload = JSON.parse(JSON.stringify(allNodesData));

    snapshotPayload.forEach((section) => {
      if (section.section_id_ref === jsonData.section_id_ref) {
        if (section.steps) {
          section.steps.forEach((step) => {
            if (step.step_id_ref === jsonData.step_id_ref) {
              let targetChildren;
              if (jsonData.substep_id_ref) {
                // Find substep
                if (step.substeps) {
                  const substep = step.substeps.find(
                    (ss) => ss.substep_id_ref === jsonData.substep_id_ref
                  );
                  if (substep) {
                    targetChildren =
                      substep.children || (substep.children = []);
                  }
                }
              } else {
                targetChildren = step.children || (step.children = []);
              }

              if (targetChildren) {
                // Adjust sibling order and insert
                adjustSiblingOrderAndInsert(targetChildren, newAttachmentNode);
              }
            }
          });
        }
      }
    });

    const previous_content_snapshot = await uploadJsonToS3(
      allNodesData,
      partnumber,
      revision + "_attachment_previous",
      true
    );

    const revAndTrainingData = await getRevAndTraining(dispatch, jsonData.part_required_id_ref)

    const new_content_snapshot = await uploadJsonToS3({
      data: snapshotPayload,
      ...ad.version_content,
      tree_node_id: treeNodeId,
      operation: "single_audit_trail",
      ...revAndTrainingData,
    },
      partnumber,
      revision + "_attachment_new",
      true
    );

    // Add audit_trail_data to JSON payload
    const payload = {
      ...jsonData,
      audit_trail_data: {
        ...ad,
        previous_content_snapshot,
        new_content_snapshot,
        version_content: {
          action_to_show: ad.version_content.action_to_show,
        }
      }
    };

    try {
      const response = await axios.post(`${baseUrl}/create`, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      
      await dispatch(
        updateCurrentSnapshot({
          partId: jsonData.part_required_id_ref,
          revisionId: jsonData.revision_id_ref,
          partNumber: partnumber,
          rev: revision,
          currentStatus: status,
          type: item_code_type,
          revAndTrainingData
        })
      ).unwrap();

      return response.data;

    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error creating attachment"
      );
    }
  }
);

// 📌 Update Attachment (PUT)
// 📌 Update Attachment (PUT)
export const updateAttachment = createAsyncThunk(
  "attachment/update",
  async (
    {
      id,
      jsonData,
      comment,
      version_content,
      allNodesData,
      partnumber,
      revision,
      parent_version,
    },
    { dispatch, rejectWithValue }
  ) => {
    const { item_code_type, status } = JSON.parse(JSON.stringify(version_content));
    let tree_node_id = "";

    const trimmedParentVersion =
      parent_version != null && String(parent_version).trim() !== ""
        ? String(parent_version).trim()
        : null;

    const ad = await getAuditTrailData({
      action: "UPDATE_ATTACHMENT",
      comment: comment,
      ...(trimmedParentVersion ? { parent_version: trimmedParentVersion } : {}),
      version_content,
    });

    const snapshotPayload = JSON.parse(JSON.stringify(allNodesData));

    // Find and update the node in snapshotPayload
    snapshotPayload.forEach((section) => {
      if (
        section.section_id_ref === jsonData.section_id_ref ||
        section.tree_node_id === jsonData.section_id_ref
      ) {
        if (section.steps) {
          section.steps.forEach((step) => {
            if (
              step.step_id_ref === jsonData.step_id_ref ||
              step.tree_node_id === jsonData.step_id_ref
            ) {
              let targetChildren;
              if (jsonData.substep_id_ref) {
                if (step.substeps) {
                  const substep = step.substeps.find(
                    (ss) =>
                      ss.substep_id_ref === jsonData.substep_id_ref ||
                      ss.tree_node_id === jsonData.substep_id_ref
                  );
                  if (substep) {
                    targetChildren = substep.children;
                  }
                }
              } else {
                targetChildren = step.children;
              }

              if (targetChildren) {
                const nodeIndex = targetChildren.findIndex(
                  (child) =>
                    child.tree_node_id === id ||
                    child.detail?.attachment_tree_node_id ===
                      jsonData.attachment_tree_node_id
                );

                if (nodeIndex !== -1) {
                  // Update the node in the snapshot path
                  const existingNode = targetChildren[nodeIndex];
                  tree_node_id = existingNode.tree_node_id;
                  
                  // Update detail properties
                  existingNode.detail = {
                    ...existingNode.detail,
                    short_description: jsonData.short_description,
                    attachments: jsonData.attachment.map((url, i) => ({
                         attachment: url,
                         // We may not have IDs for new files here, so we generate temp ones if needed
                         // but ideally we should preserve existing structure
                         attachment_id: `att_id_${Date.now()}_${i}`, 
                         attachment_tree_node_ref: jsonData.attachment_tree_node_id
                    })), 
                    // Note: This is a simplified update for the snapshot. 
                    // The actual backend update might handle things differently.
                    // Ideally we should merge with existing attachments if we had them.
                  };
                  
                  // If we have old_attachments, we might need to handle them too for a perfect snapshot,
                  // but effectively the 'jsonData.attachment' (new files) + 'jsonData.old_attachments'
                  // (if handled in UI) should form the complete list.
                  // However, jsonData.attachment represents NEW files in updateAttachment logic usually,
                  // depending on how AttachmentForm constructs it.
                  
                  // Let's re-read how AttachmentForm constructs it.
                  // It sends 'attachment' (new files) and 'old_attachments' (existing files).
                  // So we should combine them for the snapshot.
                  
                   let allAttachments = [];
                   if (jsonData.old_attachments) {
                       jsonData.old_attachments.forEach(url => {
                           allAttachments.push({ attachment: url });
                       });
                   }
                   if (jsonData.attachment) {
                       jsonData.attachment.forEach(url => {
                           allAttachments.push({ attachment: url });
                       });
                   }
                   
                   existingNode.detail.attachments = allAttachments;
                   existingNode.sibling_order = jsonData.sibling_order;
                }
              }
            }
          });
        }
      }
    });

    const previous_content_snapshot = await uploadJsonToS3(
      allNodesData,
      partnumber,
      revision + "_attachment_previous",
      true
    );

    const revAndTrainingData = await getRevAndTraining(dispatch, jsonData.part_required_id_ref)

    const new_content_snapshot = await uploadJsonToS3({
      data: snapshotPayload,
      ...ad.version_content,
      tree_node_id,
      operation: "single_audit_trail",
      ...revAndTrainingData,
    },
      partnumber,
      revision + "_attachment_new",
      true
    );

    // Add audit_trail_data to JSON payload
    const payload = {
      ...jsonData,
      audit_trail_data: {
        ...ad,
        previous_content_snapshot,
        new_content_snapshot,
        version_content: {
          action_to_show: ad.version_content.action_to_show,
        }
      }
    };

    // Ensure remove_attachment_id is an array if present
    if (payload.remove_attachment_id) {
      if (typeof payload.remove_attachment_id === "string") {
        try {
          payload.remove_attachment_id = JSON.parse(
            payload.remove_attachment_id
          );
        } catch (e) {
          // If not JSON, treat as comma-separated
          payload.remove_attachment_id =
            payload.remove_attachment_id.split(",");
        }
      }
    }

    try {
      const response = await axios.put(`${baseUrl}/update/${id}`, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });
      
      await dispatch(
        updateCurrentSnapshot({
          partId: jsonData.part_required_id_ref,
          revisionId: jsonData.revision_id_ref,
          partNumber: partnumber,
          rev: revision,
          currentStatus: status,
          type: item_code_type,
          revAndTrainingData
        })
      ).unwrap();

      return response.data;

    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error updating attachment"
      );
    }
  }
);

// 📌 Get All Attachments (GET)
export const getAllAttachments = createAsyncThunk(
  "attachment/getAll",
  async ({ limit = 10, offset = 0 }, { rejectWithValue }) => {
    try {
      const response = await axios.get(
        `${baseUrl}/get-all?limit=${limit}&offset=${offset}`
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error fetching attachments"
      );
    }
  }
);

// 📌 Delete Attachment (DELETE)
export const deleteAttachment = createAsyncThunk(
  "attachment/delete",
  async (id, { rejectWithValue }) => {
    try {
      await axios.delete(`${baseUrl}/delete/${id}`);
      return id;
    } catch (error) {
      return rejectWithValue(
        error.response?.data || "Error deleting attachment"
      );
    }
  }
);

// 🛠 Attachment Slice
const attachmentSlice = createSlice({
  name: "attachment",
  initialState: {
    attachments: [],
    loading: false,
    error: null,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(createAttachment.pending, (state) => {
        state.loading = true;
      })
      .addCase(createAttachment.fulfilled, (state, action) => {
        state.loading = false;
        state.attachments.push(action.payload);
      })
      .addCase(createAttachment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "An error occurred";
      })
      .addCase(updateAttachment.pending, (state) => {
        state.loading = true;
      })
      .addCase(updateAttachment.fulfilled, (state, action) => {
        state.loading = false;
        state.attachments = state.attachments.map((att) =>
          att.id === action.payload.id ? action.payload : att
        );
      })
      .addCase(updateAttachment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "An error occurred";
      })
      .addCase(getAllAttachments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllAttachments.fulfilled, (state, action) => {
        state.loading = false;
        state.attachments = action.payload.results;
      })
      .addCase(getAllAttachments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "An error occurred";
      })
      .addCase(deleteAttachment.pending, (state) => {
        state.loading = true;
      })
      .addCase(deleteAttachment.fulfilled, (state, action) => {
        state.loading = false;
        state.attachments = state.attachments.filter(
          (att) => att.id !== action.payload
        );
      })
      .addCase(deleteAttachment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "An error occurred";
      });
  },
});

export default attachmentSlice.reducer;
